import { BRAND, CHART_COLORS } from '@verlo/shared/lib/brand';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import {
  LayoutDashboard, Sigma, Table2, LineChart, BarChart3, PieChart, Type, Minus,
  Plus, Trash2, Settings2, X, Sparkles, Move,
  ArrowUpRight, Eye, EyeOff, CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@verlo/shared/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Button } from '@verlo/shared/components/ui/button';
import { reportsIntelApi } from '@verlo/shared/services/api';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import SavedViewChips from '../../../components/reports/SavedViewChips';
import { changedSinceSaved } from '../../../lib/savedViews';
import { formatMetric, formatRange } from '../../../components/reports/formatters';
import BenchmarkPicker from '../../../components/reports/BenchmarkPicker';
import { SitePicker } from '@verlo/shared/components/ui/site-picker';

// Convert a range key ("today", "wtd", "mtd", "ytd") into an ISO {start, end} window for benchmarking.
const rangeToPeriod = (rangeKey) => {
  const now = new Date();
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59));
  let start;
  if (rangeKey === 'today') {
    start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  } else if (rangeKey === 'yesterday') {
    start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1));
    return { start: start.toISOString(), end: new Date(start.getTime() + 86_399_000).toISOString() };
  } else if (rangeKey === 'last_week') {
    const monday = now.getUTCDate() - ((now.getUTCDay() + 6) % 7);
    start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), monday - 7));
    return { start: start.toISOString(), end: new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), monday) - 1000).toISOString() };
  } else if (rangeKey === 'last_month') {
    start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    return { start: start.toISOString(), end: new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1) - 1000).toISOString() };
  } else if (rangeKey === 'last_quarter') {
    const q = now.getUTCMonth() - (now.getUTCMonth() % 3);
    start = new Date(Date.UTC(now.getUTCFullYear(), q - 3, 1));
    return { start: start.toISOString(), end: new Date(Date.UTC(now.getUTCFullYear(), q, 1) - 1000).toISOString() };
  } else if (rangeKey === 'wtd') {
    const dow = now.getUTCDay(); // 0=Sun
    const diff = (dow + 6) % 7;   // days since Monday
    start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - diff));
  } else if (rangeKey === 'ytd') {
    start = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
  } else {
    // default & mtd
    start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  }
  return { start: start.toISOString(), end: end.toISOString() };
};

const COLS = 12;
const CELL_PX = 56; // base row height
const GAP = 12;

const BLOCK_TYPES = [
  { type: 'kpi',     icon: Sigma,      label: 'KPI tile', defaultSize: { w: 3, h: 2 }, dataBlock: true,  desc: 'Headline number with delta' },
  { type: 'line',    icon: LineChart,  label: 'Line chart', defaultSize: { w: 6, h: 4 }, dataBlock: true,  desc: 'Trend over a dimension' },
  { type: 'bar',     icon: BarChart3,  label: 'Bar chart',  defaultSize: { w: 6, h: 4 }, dataBlock: true,  desc: 'Comparison across buckets' },
  { type: 'donut',   icon: PieChart,   label: 'Donut',      defaultSize: { w: 4, h: 4 }, dataBlock: true,  desc: 'Share of total' },
  { type: 'table',   icon: Table2,     label: 'Table',      defaultSize: { w: 6, h: 4 }, dataBlock: true,  desc: 'Tabular data' },
  { type: 'text',    icon: Type,       label: 'Text',       defaultSize: { w: 6, h: 2 }, dataBlock: false, desc: 'Section title or note' },
  { type: 'divider', icon: Minus,      label: 'Divider',    defaultSize: { w: 12, h: 1 }, dataBlock: false, desc: 'Visual break' },
];

const DEFAULT_BLOCK_CONFIG = {
  metrics: ['revenue'],
  dimension: 'day',
  chart_type: 'line',
  range: 'wtd',
  site: '',
};

const newBlockId = () => `b_${Math.random().toString(36).slice(2, 10)}`;

const COLORS = CHART_COLORS;
const DONUT_COLORS = CHART_COLORS;

// ---------- Atomic block renderers ----------

const KpiBlockRender = ({ data, title, why }) => {
  if (!data?.summary?.length) return <BlockEmpty why={why} />;
  const s = data.summary[0];
  const delta = s.delta_pct;
  const good = delta == null ? null : delta > 0;
  return (
    <div className="flex flex-col justify-between h-full p-4">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft">
        {title || s.label}
      </div>
      <div>
        <div className="font-mono-display text-3xl font-medium tabular-nums text-ink">
          {formatMetric(s.value, s.format)}
        </div>
        {delta != null && (
          <div className={`text-[12.5px] mt-0.5 font-mono-display flex items-center gap-0.5 whitespace-nowrap overflow-hidden ${good ? 'text-signal-ok' : 'text-signal-stop'}`}>
            <ArrowUpRight size={11} className={good ? '' : 'rotate-90'} />
            {good ? '+' : ''}{delta.toFixed(1)}%<span className="truncate">&nbsp;vs previous</span>
          </div>
        )}
      </div>
    </div>
  );
};

const LineBarBlockRender = ({ data, kind = 'line', title, why }) => {
  const buckets = data?.series?.[0]?.buckets || [];
  if (!buckets.length) return <BlockEmpty why={why} />;
  const W = 720, H = 200, pad = 28;
  // Bars stand in the middle of equal slots, so the first and last aren't cut in half by the
  // edges; a line runs edge to edge.
  const slot = kind === 'bar' ? (W - pad - 8) / buckets.length : (W - pad - 8) / Math.max(buckets.length - 1, 1);
  const xAt = (i) => pad + (kind === 'bar' ? (i + 0.5) * slot : i * slot);
  return (
    <div className="h-full flex flex-col p-3">
      {title && <div className="text-[12.5px] font-semibold text-ink-soft mb-1">{title}</div>}
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full flex-1 min-h-0" preserveAspectRatio="none">
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
          <line key={i} x1={pad} x2={W - 8} y1={pad + (H - pad * 2) * t} y2={pad + (H - pad * 2) * t}
                stroke="#e2e8f0" strokeDasharray="2 4" />
        ))}
        {data.series.map((s, si) => {
          const values = s.buckets.map((b) => b.value);
          const max = Math.max(...values, 0.01);
          const min = Math.min(...values, 0);
          const range = max - min || 1;
          const step = slot;
          const points = values.map((v, i) => {
            const x = xAt(i);
            const y = pad + (H - pad * 2) * (1 - (v - min) / range);
            return [x, y];
          });
          if (kind === 'bar') {
            const bw = Math.max(step * 0.55 / data.series.length, 3);
            return (
              <g key={s.metric}>
                {points.map(([x, y], i) => (
                  <rect
                    key={i}
                    x={x - bw / 2 + si * bw * 1.05 - ((data.series.length - 1) * bw * 1.05) / 2}
                    y={y}
                    width={bw}
                    height={Math.max(0, H - pad - y)}
                    fill={COLORS[si % COLORS.length]}
                    opacity={0.85}
                    rx={2}
                  />
                ))}
              </g>
            );
          }
          const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ');
          return (
            <g key={s.metric}>
              <path d={path} stroke={COLORS[si % COLORS.length]} strokeWidth={2} fill="none" strokeLinecap="round" />
              {points.map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r={2.5} fill="white" stroke={COLORS[si % COLORS.length]} strokeWidth={1.5} />
              ))}
            </g>
          );
        })}
      </svg>
      {/* The labels sit under the drawing, not in it: the drawing stretches to its block, and
          text inside it would stretch with it. */}
      <div className="relative h-4 shrink-0 text-[11px] text-ink-faint" aria-hidden>
        {buckets.map((b, i) => {
          if (buckets.length > 10 && i % 2 !== 0) return null;
          return (
            <span key={i} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${(xAt(i) / W) * 100}%` }}>{b.label}</span>
          );
        })}
      </div>
      <div className="flex gap-2 flex-wrap mt-1">
        {data.series.map((s, i) => (
          <span key={s.metric} className="flex items-center gap-1 text-[12.5px] text-ink-soft">
            <span className="w-2 h-2 rounded-md" style={{ background: COLORS[i % COLORS.length] }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const DonutBlockRender = ({ data, title, why }) => {
  const s = data?.series?.[0];
  if (!s?.buckets?.length) return <BlockEmpty why={why} />;
  const total = s.buckets.reduce((sum, b) => sum + Math.max(b.value, 0), 0) || 1;
  let cum = 0;
  const r = 60;
  const cx = 70, cy = 70;
  const slices = s.buckets.map((b, i) => {
    const frac = Math.max(b.value, 0) / total;
    const startA = cum * 2 * Math.PI - Math.PI / 2;
    const endA = (cum + frac) * 2 * Math.PI - Math.PI / 2;
    cum += frac;
    const x1 = cx + r * Math.cos(startA), y1 = cy + r * Math.sin(startA);
    const x2 = cx + r * Math.cos(endA),   y2 = cy + r * Math.sin(endA);
    const large = frac > 0.5 ? 1 : 0;
    return {
      d: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`,
      fill: DONUT_COLORS[i % DONUT_COLORS.length],
      label: b.label, value: b.value, pct: (frac * 100).toFixed(1),
    };
  });
  return (
    <div className="h-full p-3 flex items-center gap-3">
      <svg viewBox="0 0 140 140" className="w-32 h-32 shrink-0">
        {slices.map((sl, i) => <path key={i} d={sl.d} fill={sl.fill} stroke="white" strokeWidth={1.5} />)}
        <circle cx={cx} cy={cy} r="35" fill="white" />
        <text x={cx} y={cy - 2} textAnchor="middle" fontSize="8" fill="#94a3b8" className="uppercase tracking-wider font-semibold">{title || s.label}</text>
        <text x={cx} y={cy + 13} textAnchor="middle" fontSize="13" fill={BRAND.verlo} className="font-semibold font-mono">{formatMetric(total, s.format)}</text>
      </svg>
      <ul className="space-y-1 flex-1 overflow-y-auto max-h-full">
        {slices.map((sl, i) => (
          <li key={i} className="flex items-center justify-between gap-2 text-[12.5px]">
            <span className="flex items-center gap-1.5 text-ink-soft truncate">
              <span className="w-2 h-2 rounded-md shrink-0" style={{ background: sl.fill }} />
              <span className="truncate">{sl.label}</span>
            </span>
            <span className="font-mono-display text-ink text-[12.5px] shrink-0">{sl.pct}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const TableBlockRender = ({ data, title, why }) => {
  const series = data?.series || [];
  if (!series.length) return <BlockEmpty why={why} />;
  return (
    <div className="h-full overflow-auto p-3">
      {title && <div className="text-[12.5px] font-semibold text-ink-soft mb-2">{title}</div>}
      <table className="w-full text-[12.5px]">
        <thead className="bg-surface-2 sticky top-0">
          <tr>
            <th className="text-left px-2 py-1.5 font-semibold text-ink-soft">Bucket</th>
            {series.map((s) => (
              <th key={s.metric} className="text-right px-2 py-1.5 font-semibold text-ink-soft">{s.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(series[0]?.buckets || []).map((_, i) => (
            <tr key={i} className="border-t border-hairline">
              <td className="px-2 py-1 text-ink-soft">{series[0].buckets[i].label}</td>
              {series.map((s) => (
                <td key={s.metric} className="px-2 py-1 text-right font-mono-display text-ink">
                  {formatMetric(s.buckets[i]?.value, s.format)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TextBlockRender = ({ block }) => (
  <div className="h-full p-4 flex flex-col justify-center">
    {block.text?.title && (
      <h4 className="text-base font-semibold text-ink mb-1">{block.text.title}</h4>
    )}
    {block.text?.body && (
      <p className="text-[12px] text-ink-soft leading-relaxed whitespace-pre-wrap">{block.text.body}</p>
    )}
    {!block.text?.title && !block.text?.body && (
      <p className="text-[12px] text-ink-faint italic">Click to add text…</p>
    )}
  </div>
);

const DividerBlockRender = () => (
  <div className="h-full px-3 flex items-center"><div className="w-full h-px bg-hairline" /></div>
);

// An empty block says why, when it can: "No Fridays in this week so far" rather than a blank.
const BlockEmpty = ({ why }) => (
  <div className="h-full flex flex-col items-center justify-center text-center px-4" data-testid="builder-v2-block-empty">
    {!why && <Sparkles size={20} className="text-ink-faint mb-1.5" />}
    <p className="text-[12.5px] text-ink-faint">{why || 'Configure this block to see data'}</p>
  </div>
);

const DAY_WORDS = {
  mon: 'Mondays', tue: 'Tuesdays', wed: 'Wednesdays', thu: 'Thursdays', fri: 'Fridays',
  sat: 'Saturdays', sun: 'Sundays', weekday: 'weekdays', weekend: 'weekend days',
};
const RANGE_WORDS = {
  today: 'today', yesterday: 'yesterday', wtd: 'this week so far', last_week: 'last week',
  mtd: 'this month so far', last_month: 'last month', ytd: 'this year so far',
};
// Ranges too short to be sure of a given weekday. Picking a weekday moves these to 12 weeks.
const SHORT_RANGES = new Set(['today', 'yesterday', 'wtd', 'last_week', 'last_7', 'mtd']);

const emptyWhy = (config) => {
  const when = RANGE_WORDS[config?.range] || formatRange(config?.range || 'wtd').toLowerCase();
  const day = DAY_WORDS[config?.weekday_filter];
  return day ? `No ${day} in ${when}` : `Nothing sold ${RANGE_WORDS[config?.range] ? '' : 'in '}${when}`;
};

// ---------- Main page ----------

// The dashboard open last, opened again next time (per browser).
const LAST_DASHBOARD_KEY = 'verlo.dashboards.lastView';

const ReportBuilderV2Page = () => {
  const [catalog, setCatalog] = useState({ metrics: [], dimensions: [], chart_types: [] });
  const [sites, setSites] = useState([]);
  const [views, setViews] = useState([]);
  const [activeView, setActiveView] = useState(null); // currently loaded view doc
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canSchedule = hasPermission('reports.view_schedules');
  const [layout, setLayout] = useState([]);           // current blocks
  const [previews, setPreviews] = useState({});       // block.id -> preview
  const [previewing, setPreviewing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [presenting, setPresenting] = useState(false); // hide chrome for screenshots
  // drag state
  const [drag, setDrag] = useState(null); // { id, mode: 'move'|'resize', startX, startY, startBlock, gridLeft, cellW }
  const canvasRef = useRef(null);

  const selected = useMemo(() => layout.find((b) => b.id === selectedId) || null, [layout, selectedId]);
  const dashboards = useMemo(() => views.filter((v) => v.version === 2 && Array.isArray(v.layout)), [views]);

  // ---------- Load catalogue / views / sites ----------
  useEffect(() => {
    (async () => {
      try {
        const [c, v, s] = await Promise.all([
          reportsIntelApi.builderCatalog(),
          reportsIntelApi.listBuilderViews(),
          reportsIntelApi.listSites(),
        ]);
        setCatalog(c.data);
        setViews(v.data.views || []);
        setSites(s.data.sites || []);
        // A link to a dashboard (a pinned one on Reports, say) opens it.
        const linked = new URLSearchParams(window.location.search).get('view');
        let last = null;
        try { last = localStorage.getItem(LAST_DASHBOARD_KEY); } catch { /* storage off */ }
        // A linked one first, else the one you had open last (D5).
        const view = (v.data.views || []).find((x) => x.id === (linked || last) && x.version === 2);
        if (view) onLoadView(view);
      } catch {
        toast.error('Could not load Report Builder');
      }
    })();
  }, []);

  // ---------- Bulk preview for all data blocks (debounced) ----------
  useEffect(() => {
    const dataBlocks = layout.filter((b) => ['kpi', 'line', 'bar', 'donut', 'table'].includes(b.type) && b.config);
    if (dataBlocks.length === 0) { setPreviews({}); return; }
    const t = setTimeout(async () => {
      setPreviewing(true);
      try {
        const blocks = dataBlocks.map((b) => ({ id: b.id, config: { ...b.config, chart_type: b.config.chart_type || b.type } }));
        const res = await reportsIntelApi.bulkPreviewBuilder(blocks);
        setPreviews(res.data.blocks || {});
      } catch (e) {
        // Don't toast on every keystroke
        // eslint-disable-next-line no-console
        console.warn('preview failed', e?.response?.data);
      } finally { setPreviewing(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [layout]);

  // ---------- Helpers ----------

  const placeBlockAtBottom = (size) => {
    // Find max y currently used, place new block on its own row at x=0
    if (layout.length === 0) return { x: 0, y: 0 };
    const bottomY = Math.max(...layout.map((b) => b.y + b.h));
    return { x: 0, y: bottomY };
  };

  const addBlock = (typeKey) => {
    const meta = BLOCK_TYPES.find((b) => b.type === typeKey);
    if (!meta) return;
    const { x, y } = placeBlockAtBottom(meta.defaultSize);
    const block = {
      id: newBlockId(),
      type: typeKey,
      x, y,
      w: meta.defaultSize.w,
      h: meta.defaultSize.h,
      title: null,
      ...(meta.dataBlock
        ? { config: { ...DEFAULT_BLOCK_CONFIG, chart_type: typeKey === 'kpi' || typeKey === 'table' ? typeKey : typeKey } }
        : { text: typeKey === 'text' ? { title: 'New section', body: '' } : null }),
    };
    setLayout((l) => [...l, block]);
    setSelectedId(block.id);
  };

  const updateBlock = (id, patch) => {
    setLayout((l) => l.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const updateBlockConfig = (id, patch) => {
    setLayout((l) => l.map((b) => (b.id === id ? { ...b, config: { ...b.config, ...patch } } : b)));
  };

  const removeBlock = (id) => {
    setLayout((l) => l.filter((b) => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const duplicateBlock = (id) => {
    const b = layout.find((x) => x.id === id);
    if (!b) return;
    const copy = { ...b, id: newBlockId(), x: 0, y: Math.max(...layout.map((bb) => bb.y + bb.h)) };
    setLayout((l) => [...l, copy]);
    setSelectedId(copy.id);
  };

  // ---------- Drag & resize handlers ----------

  const onPointerDown = (e, block, mode) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(block.id);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cellW = (rect.width - GAP * (COLS - 1)) / COLS;
    setDrag({
      id: block.id,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      startBlock: { ...block },
      cellW,
    });
  };

  useEffect(() => {
    if (!drag) return;
    const move = (e) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const dCol = Math.round(dx / (drag.cellW + GAP));
      const dRow = Math.round(dy / (CELL_PX + GAP));
      if (drag.mode === 'move') {
        const newX = Math.max(0, Math.min(COLS - drag.startBlock.w, drag.startBlock.x + dCol));
        const newY = Math.max(0, drag.startBlock.y + dRow);
        updateBlock(drag.id, { x: newX, y: newY });
      } else if (drag.mode === 'resize') {
        const newW = Math.max(1, Math.min(COLS - drag.startBlock.x, drag.startBlock.w + dCol));
        const newH = Math.max(1, drag.startBlock.h + dRow);
        updateBlock(drag.id, { w: newW, h: newH });
      }
    };
    const up = () => setDrag(null);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    // eslint-disable-next-line
  }, [drag]);

  // ---------- Save / Load ----------

  const totalRows = useMemo(() => Math.max(8, ...layout.map((b) => b.y + b.h)), [layout]);

  const onSave = async () => {
    if (saving) return;
    if (!saveName.trim()) { toast.error('Name required'); return; }
    if (layout.length === 0) { toast.error('Add at least one block'); return; }
    setSaving(true);
    try {
      const res = await reportsIntelApi.createBuilderView({ name: saveName.trim(), version: 2, layout });
      setActiveView(res.data);
      try { localStorage.setItem(LAST_DASHBOARD_KEY, res.data.id); } catch { /* storage off */ }
      toast.success('Dashboard saved', { id: 'builder-v2-save' });
      const list = await reportsIntelApi.listBuilderViews();
      setViews(list.data.views || []);
      setSaveOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  // One saved dashboard changed: the list and, if it's the open one, the canvas's copy.
  const replaceView = (view) => {
    setViews((vs) => vs.map((v) => (v.id === view.id ? view : v)));
    if (activeView?.id === view.id) setActiveView(view);
  };

  const onUpdate = async (view) => {
    if (layout.length === 0) { toast.error('Add at least one block'); return; }
    try {
      const res = await reportsIntelApi.updateBuilderView(view.id, { layout });
      replaceView(res.data);
      toast.success(`${view.name} updated`, { id: 'builder-v2-save' });
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update it"); }
  };

  const onRename = async (view, name) => {
    try {
      replaceView((await reportsIntelApi.updateBuilderView(view.id, { name })).data);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't rename it"); }
  };

  const onTogglePin = async (view) => {
    try {
      const res = await reportsIntelApi.updateBuilderView(view.id, { pinned: !view.pinned });
      replaceView(res.data);
      toast.success(res.data.pinned ? 'Pinned to the Executive dashboard' : 'Unpinned');
    } catch { toast.error('Could not update'); }
  };

  // The chip's menu asks first.
  const onDelete = async (view) => {
    try {
      await reportsIntelApi.deleteBuilderView(view.id);
      setViews((vs) => vs.filter((v) => v.id !== view.id));
      if (activeView?.id === view.id) { setActiveView(null); setLayout([]); setSelectedId(null); }
    } catch { toast.error("Couldn't delete it"); }
  };

  const onLoadView = (view) => {
    if (view.version === 2 && Array.isArray(view.layout)) {
      setLayout(view.layout);
      setActiveView(view);
      setSaveName(view.name);
      setSelectedId(null);
      try { localStorage.setItem(LAST_DASHBOARD_KEY, view.id); } catch { /* storage off */ }
    } else {
      toast.error('That is a Classic view. Open it in Classic Builder.');
    }
  };

  // New asks before throwing away anything not saved: a new canvas, or changes to a saved one.
  const onNewDashboard = () => {
    const unsaved = layout.length > 0 && (!activeView || changedSinceSaved(activeView.layout, layout, sites));
    if (unsaved && !window.confirm(activeView ? `Discard your changes to ${activeView.name}?` : 'Discard this unsaved dashboard?')) return;
    setLayout([]);
    setActiveView(null);
    setSaveName('');
    setSelectedId(null);
  };

  // ---------- Render ----------

  const renderBlockBody = (block) => {
    const preview = previews[block.id];
    if (block.type === 'text') return <TextBlockRender block={block} />;
    if (block.type === 'divider') return <DividerBlockRender />;
    if (!preview) return <BlockEmpty />;
    const why = emptyWhy(block.config);
    if (block.type === 'kpi')   return <KpiBlockRender data={preview} title={block.title} why={why} />;
    if (block.type === 'line')  return <LineBarBlockRender data={preview} kind="line" title={block.title} why={why} />;
    if (block.type === 'bar')   return <LineBarBlockRender data={preview} kind="bar" title={block.title} why={why} />;
    if (block.type === 'donut') return <DonutBlockRender data={preview} title={block.title} why={why} />;
    if (block.type === 'table') return <TableBlockRender data={preview} title={block.title} why={why} />;
    return null;
  };

  return (
    <ReportsLayout>
      <div data-testid="builder-v2-page">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap" data-testid="builder-v2-toolbar">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Saved dashboards along the top (D5): the last one used opens first. */}
            <SavedViewChips
              views={dashboards}
              activeId={activeView?.id}
              dirty={!!activeView && changedSinceSaved(activeView.layout, layout, sites)}
              onOpen={onLoadView}
              onSave={() => { setSaveName(''); setSaveOpen(true); }}
              onUpdate={onUpdate}
              onRename={onRename}
              onPin={onTogglePin}
              onDelete={onDelete}
              saveLabel="+ Save as a dashboard"
              noun="dashboard"
              testId="builder-v2-views"
              saveTestId="builder-v2-save"
            />
            <button
              onClick={() => setPresenting((p) => !p)}
              className={`inline-flex items-center gap-1 text-[12.5px] px-2 py-1 rounded border ${
                presenting ? 'bg-ink text-white border-ink' : 'bg-white text-ink-soft border-hairline hover:border-ink-faint'
              }`}
              data-testid="builder-v2-present-toggle"
              title="Toggle present mode"
            >
              {presenting ? <EyeOff size={11} /> : <Eye size={11} />}
              {presenting ? 'Edit' : 'Present'}
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <a href="/back-office/reports/builder" className="text-[12.5px] text-ink-soft hover:text-ink underline-offset-2 hover:underline mr-1" data-testid="builder-v2-classic-link">Classic builder</a>
            <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={onNewDashboard} data-testid="builder-v2-new">
              <Plus size={13} /> New
            </Button>
            {activeView && canSchedule && (
              <Button
                variant="outline" size="sm" className="h-9 gap-1.5"
                onClick={() => navigate(`/back-office/reports/schedules?view=${activeView.id}`)}
                data-testid="builder-v2-schedule"
              >
                <CalendarClock size={13} /> Schedule
              </Button>
            )}
          </div>
        </div>

        <div className={`grid gap-4 ${presenting ? '' : 'lg:grid-cols-[200px_1fr_280px]'}`}>
          {/* Left palette */}
          {!presenting && (
            <div className="space-y-3" data-testid="builder-v2-palette">
              <div className="bg-white border border-hairline rounded-lg p-3">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft mb-2">Add a block</div>
                <div className="space-y-1.5">
                  {BLOCK_TYPES.map((bt) => {
                    const Icon = bt.icon;
                    return (
                      <button
                        key={bt.type}
                        onClick={() => addBlock(bt.type)}
                        className="w-full flex items-start gap-2 px-2.5 py-2 text-left rounded-md hover:bg-hover-tint border border-transparent hover:border-hairline transition-colors"
                        data-testid={`builder-v2-add-${bt.type}`}
                      >
                        <span className="w-7 h-7 rounded-md bg-paper flex items-center justify-center shrink-0">
                          <Icon size={13} className="text-verlo" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="text-[12px] font-semibold text-ink block">{bt.label}</span>
                          <span className="text-[12.5px] text-ink-soft block leading-tight">{bt.desc}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="bg-surface-2 border border-hairline rounded-lg p-3 text-[12.5px] text-ink-soft leading-relaxed">
                Drag the move handle on each block to reposition. Drag the corner to resize. Click a block to configure it on the right.
              </div>
            </div>
          )}

          {/* Canvas */}
          <div
            ref={canvasRef}
            className={`relative rounded-lg ${presenting ? 'bg-transparent' : 'bg-surface-2/60 border border-hairline p-3'}`}
            style={{ minHeight: totalRows * (CELL_PX + GAP) }}
            data-testid="builder-v2-canvas"
            onClick={() => setSelectedId(null)}
          >
            {/* Grid hint */}
            {!presenting && layout.length === 0 && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
                <LayoutDashboard size={32} className="text-ink-faint mb-3" />
                <p className="text-[14px] text-ink-soft font-semibold">Build your custom dashboard</p>
                <p className="text-[12px] text-ink-faint mt-1 max-w-xs">Pick a block from the left to add it. Stack KPIs, charts and notes in any layout.</p>
              </div>
            )}
            {/* Grid background */}
            {!presenting && layout.length > 0 && (
              <div className="absolute inset-3 grid pointer-events-none" style={{
                gridTemplateColumns: `repeat(${COLS}, 1fr)`,
                gridTemplateRows: `repeat(${totalRows}, ${CELL_PX}px)`,
                gap: `${GAP}px`,
              }}>
                {Array.from({ length: COLS * totalRows }).map((_, i) => (
                  <div key={i} className="bg-white/40 border border-dashed border-hairline/70 rounded-md" />
                ))}
              </div>
            )}
            {/* Blocks */}
            <div className="relative grid" style={{
              gridTemplateColumns: `repeat(${COLS}, 1fr)`,
              gridAutoRows: `${CELL_PX}px`,
              gap: `${GAP}px`,
            }}>
              {layout.map((b) => {
                const isSelected = b.id === selectedId;
                return (
                  <div
                    key={b.id}
                    className={`bg-white border rounded-lg overflow-hidden relative transition-shadow ${
                      isSelected && !presenting ? 'border-verlo shadow-lg ring-2 ring-verlo/15' : 'border-hairline hover:shadow'
                    }`}
                    style={{
                      gridColumnStart: b.x + 1,
                      gridColumnEnd: b.x + 1 + b.w,
                      gridRowStart: b.y + 1,
                      gridRowEnd: b.y + 1 + b.h,
                    }}
                    onClick={(e) => { e.stopPropagation(); setSelectedId(b.id); }}
                    data-testid={`builder-v2-block-${b.id}`}
                  >
                    {/* Move handle + actions */}
                    {!presenting && (
                      <div className="absolute top-0 left-0 right-0 flex items-center gap-1 px-1.5 py-1 bg-white/90 backdrop-blur-sm border-b border-hairline z-10">
                        <button
                          onPointerDown={(e) => onPointerDown(e, b, 'move')}
                          className="cursor-grab active:cursor-grabbing px-1 py-0.5 text-ink-faint hover:text-ink"
                          title="Drag to move"
                          data-testid={`builder-v2-move-${b.id}`}
                        >
                          <Move size={11} />
                        </button>
                        <span className="text-[11px] font-semibold text-ink-soft uppercase tracking-wider truncate flex-1">
                          {b.title || BLOCK_TYPES.find((x) => x.type === b.type)?.label || b.type}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); duplicateBlock(b.id); }}
                          className="px-1 py-0.5 text-ink-faint hover:text-ink"
                          title="Duplicate"
                          data-testid={`builder-v2-dup-${b.id}`}
                        >
                          <Plus size={11} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeBlock(b.id); }}
                          className="px-1 py-0.5 text-ink-faint hover:text-signal-stop"
                          title="Remove"
                          data-testid={`builder-v2-del-${b.id}`}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    )}
                    <div className={`h-full ${presenting ? '' : 'pt-6'}`}>
                      {renderBlockBody(b)}
                    </div>
                    {/* Resize handle */}
                    {!presenting && (
                      <div
                        onPointerDown={(e) => onPointerDown(e, b, 'resize')}
                        className="absolute right-0 bottom-0 w-4 h-4 cursor-nwse-resize bg-transparent"
                        title="Drag to resize"
                        data-testid={`builder-v2-resize-${b.id}`}
                      >
                        <svg viewBox="0 0 10 10" className="w-full h-full text-ink-faint">
                          <path d="M9 1 L1 9 M9 4 L4 9 M9 7 L7 9" stroke="currentColor" strokeWidth="1" fill="none" />
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {previewing && (
              <div className="absolute top-3 right-3 text-[12.5px] text-ink-faint bg-white/80 px-2 py-1 rounded-full border border-hairline">
                Updating…
              </div>
            )}
          </div>

          {/* Right inspector */}
          {!presenting && (
            <div className="space-y-3" data-testid="builder-v2-inspector">
              {!selected && (
                <div className="bg-white border border-hairline rounded-lg p-4">
                  <Settings2 size={18} className="text-ink-faint mb-2" />
                  <div className="text-[13px] font-semibold text-ink">Inspector</div>
                  <p className="text-[12.5px] text-ink-soft mt-1 leading-relaxed">Select a block to configure its metric, dimension, time range and site.</p>
                </div>
              )}
              {selected && (
                <Inspector
                  block={selected}
                  catalog={catalog}
                  sites={sites}
                  preview={previews[selected.id]}
                  onUpdate={(patch) => updateBlock(selected.id, patch)}
                  onUpdateConfig={(patch) => updateBlockConfig(selected.id, patch)}
                  onRemove={() => removeBlock(selected.id)}
                />
              )}
            </div>
          )}
        </div>

        {/* Save dialog */}
        <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
          <DialogContent className="max-w-md" data-testid="builder-v2-save-dialog">
            <DialogHeader>
              <DialogTitle>Save as a dashboard</DialogTitle>
              <DialogDescription>
                Saved dashboards can be opened by anyone with Report Builder access. Pin to Executive for one-click access from the home screen.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5 py-2">
              <Label htmlFor="dash-name" className="text-[12px]">Dashboard name</Label>
              <Input
                id="dash-name"
                placeholder="e.g. Friday night ops control"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                data-testid="builder-v2-save-name"
              />
              <p className="text-[12.5px] text-ink-soft mt-1">{layout.length} block{layout.length === 1 ? '' : 's'} will be saved.</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setSaveOpen(false)}>Cancel</Button>
              <Button className="bg-verlo hover:bg-verlo-hover" onClick={onSave} disabled={saving} data-testid="builder-v2-save-confirm">
                {saving ? 'Saving…' : 'Save dashboard'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </ReportsLayout>
  );
};

// ---------- Inspector ----------

const Inspector = ({ block, catalog, sites, preview, onUpdate, onUpdateConfig, onRemove }) => {
  const meta = BLOCK_TYPES.find((b) => b.type === block.type);
  const Icon = meta?.icon || Sigma;
  const isData = ['kpi', 'line', 'bar', 'donut', 'table'].includes(block.type);
  const isText = block.type === 'text';
  const weekdayFilters = catalog.weekday_filters || [];
  const kpiSummary = block.type === 'kpi' ? preview?.summary?.[0] : null;

  return (
    <div className="bg-white border border-hairline rounded-lg overflow-hidden">
      <div className="px-3 py-2.5 border-b border-hairline flex items-center gap-2">
        <span className="w-7 h-7 rounded-md bg-paper flex items-center justify-center">
          <Icon size={13} className="text-verlo" />
        </span>
        <span className="text-[12px] font-semibold text-ink flex-1">{meta?.label || block.type}</span>
        <button onClick={onRemove} className="text-ink-faint hover:text-signal-stop" data-testid="builder-v2-inspector-remove">
          <Trash2 size={12} />
        </button>
      </div>
      <div className="p-3 space-y-3">
        <div>
          <Label className="text-[12.5px]">Title <span className="text-ink-faint">· optional</span></Label>
          <Input
            placeholder={meta?.label || 'Block title'}
            value={block.title || ''}
            onChange={(e) => onUpdate({ title: e.target.value || null })}
            className="h-8 mt-1 text-[12px]"
            data-testid="builder-v2-inspector-title"
          />
        </div>

        {isText && (
          <>
            <div>
              <Label className="text-[12.5px]">Heading</Label>
              <Input
                placeholder="Section heading"
                value={block.text?.title || ''}
                onChange={(e) => onUpdate({ text: { ...(block.text || {}), title: e.target.value } })}
                className="h-8 mt-1 text-[12px]"
                data-testid="builder-v2-inspector-text-title"
              />
            </div>
            <div>
              <Label className="text-[12.5px]">Body</Label>
              <textarea
                placeholder="Add a note for the team…"
                value={block.text?.body || ''}
                onChange={(e) => onUpdate({ text: { ...(block.text || {}), body: e.target.value } })}
                className="w-full mt-1 text-[12px] border border-hairline rounded-md px-2 py-1.5 min-h-[80px] resize-y"
                data-testid="builder-v2-inspector-text-body"
              />
            </div>
          </>
        )}

        {isData && (
          <>
            <div>
              <Label className="text-[12.5px]">Metric{block.type === 'kpi' ? '' : 's'}</Label>
              <div className="mt-1 max-h-[180px] overflow-y-auto border border-hairline rounded-md divide-y divide-hairline">
                {catalog.metrics.map((m) => {
                  const checked = block.config?.metrics?.includes(m.key);
                  return (
                    <button
                      key={m.key}
                      onClick={() => {
                        let next = block.config?.metrics || [];
                        if (block.type === 'kpi') {
                          next = [m.key]; // single metric for KPI
                        } else if (checked) {
                          next = next.filter((k) => k !== m.key);
                          if (!next.length) next = [m.key]; // keep at least one
                        } else {
                          next = [...next, m.key];
                        }
                        onUpdateConfig({ metrics: next });
                      }}
                      className={`w-full flex items-center justify-between px-2 py-1.5 text-[12.5px] text-left transition-colors ${checked ? 'bg-verlo/5 text-ink font-semibold' : 'text-ink-soft hover:bg-hover-tint'}`}
                      data-testid={`builder-v2-inspector-metric-${m.key}`}
                    >
                      <span>{m.label}</span>
                      <span className="text-[11px] uppercase text-ink-faint font-mono-display">{m.category}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {['line', 'bar', 'donut', 'table'].includes(block.type) && (
              <div>
                <Label className="text-[12.5px]">Group by</Label>
                <Select value={block.config?.dimension || ''} onValueChange={(v) => onUpdateConfig({ dimension: v })}>
                  <SelectTrigger className="h-8 mt-1 text-[12px]" data-testid="builder-v2-inspector-dim"><SelectValue placeholder="Pick a dimension" /></SelectTrigger>
                  <SelectContent>
                    {catalog.dimensions.map((d) => (
                      <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[12.5px]">Range</Label>
                <Select value={block.config?.range} onValueChange={(v) => onUpdateConfig({ range: v })}>
                  <SelectTrigger className="h-8 mt-1 text-[12px]" data-testid="builder-v2-inspector-range"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(catalog.ranges || ['today', 'wtd', 'mtd', 'ytd']).map((r) => (
                      <SelectItem key={r} value={r}>{formatRange(r)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12.5px]">Site</Label>
                <SitePicker sites={sites} value={block.config?.site} onChange={(v) => onUpdateConfig({ site: v })} triggerClassName="h-8 mt-1 text-[12px]" testId="builder-v2-inspector-site" />
              </div>
            </div>

            {weekdayFilters.length > 0 && (
              <div>
                <Label className="text-[12.5px]">Days</Label>
                <Select
                  value={block.config?.weekday_filter || 'any'}
                  onValueChange={(v) => onUpdateConfig(
                    v === 'any' ? { weekday_filter: null }
                    // A week to date can hold no Friday at all, so a weekday looks back 12 weeks.
                    : SHORT_RANGES.has(block.config?.range) ? { weekday_filter: v, range: 'last_12_weeks' }
                    : { weekday_filter: v },
                  )}
                >
                  <SelectTrigger className="h-8 mt-1 text-[12px]" data-testid="builder-v2-inspector-weekday">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {weekdayFilters.map((wf) => (
                      <SelectItem key={wf.key} value={wf.key}>{wf.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[12.5px] text-ink-faint mt-1">Only these days count. A short range moves to the last 12 weeks, so there's enough of them to show.</p>
              </div>
            )}

            {block.type === 'kpi' && kpiSummary && (
              <div className="pt-2 border-t border-hairline" data-testid="builder-v2-inspector-benchmark">
                <BenchmarkPicker
                  metric={block.config?.metrics?.[0] || 'revenue'}
                  currentValue={kpiSummary.value}
                  siteId={block.config?.site}
                  period={rangeToPeriod(block.config?.range || 'wtd')}
                  format={kpiSummary.format === 'percent' ? 'raw' : 'gbp'}
                  label="Benchmark"
                />
              </div>
            )}
          </>
        )}

        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-hairline">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-ink-soft">Width</Label>
            <p className="text-[12px] font-mono-display font-semibold text-ink mt-0.5">{block.w} <span className="text-ink-faint">/ 12</span></p>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-ink-soft">Height</Label>
            <p className="text-[12px] font-mono-display font-semibold text-ink mt-0.5">{block.h} cells</p>
          </div>
        </div>
        <p className="text-[12.5px] text-ink-faint leading-relaxed">Tip: drag the bottom-right corner of the block to resize.</p>
      </div>
    </div>
  );
};

export default ReportBuilderV2Page;
